(ns graph-ui.web.markdown
  "Markdown → hiccup renderer using marked's lexer (ADR-graph-ui-v0-012-01 Step 5).
   Replaces the hand-rolled 8-regex parser that caused a production crash.
   Covers the same vault constructs: headings, lists, fenced code, blockquotes,
   horizontal rules, GFM tables, paragraphs; inline bold, italic, code, links,
   [[wikilinks]] and Foam #hashtags."
  (:require [clojure.string :as str]
            [graph-ui.web.note-image :as note-image]
            ["marked" :refer [lexer Lexer]]))

;; @feature F-graph-ui-v0-002-01-foam-markdown-vault
;; @adr ADR-ikc-v3-052-01-foam-markdown-vault
;; @adr ADR-graph-ui-v0-012-01-mantine-component-library
;; @adr ADR-graph-ui-v0-017-01-highlight-capture-and-paint

;; -----------------------------------------------------------------------------
;; Wikilink pre-processing: turn [[target]] into a marked link so the real
;; parser handles all other inline rules, then we special-case wikilink: URLs.
;; -----------------------------------------------------------------------------

(def ^:private wiki-re
  "Match [[target|label]] or [[target]]; label is optional."
  #"\[\[([^\]|]+)(?:\|([^\]]+))?\]\]")

(defn- wikify
  "Replace [[target|label]] with [label](wikilink:target) for marked lexing.

   Returns `[rewritten edits]`, where each edit is `{:orig-end <position in the
   ORIGINAL string just past the [[link]]> :delta <length change>}`, ascending.

   WHY IT REPORTS ITS OWN EDITS. Highlights are stored as offsets into the
   ORIGINAL text, and every offset the lexer later reports is into THIS string —
   so without a record of what moved, an offset-mapped paint would be wrong by
   the accumulated length of every wikilink above it, and wrong by more the
   further down the document you read (F-platform-v0-011-01). A blind
   str/replace cannot say what it moved; this can."
  [s]
  (let [re (js/RegExp. (.-source wiki-re) "g")]
    (loop [last-end 0, out "", edits []]
      (if-let [m (.exec re s)]
        (let [start       (.-index m)
              matched     (aget m 0)
              target      (aget m 1)
              label       (aget m 2)
              ;; THE DESTINATION IS PERCENT-ENCODED, AND A MULTI-WORD PAGE
              ;; NAME IS WHY (2026-09-07). It was the bare target, so
              ;; `[[No Such Page Yet]]` became
              ;; `[No Such Page Yet](wikilink:No Such Page Yet)` — and a
              ;; markdown link destination may not hold unescaped spaces, so
              ;; marked refused to lex it as a link at all. The whole rewrite
              ;; then reached the screen as literal text: no chip, no click,
              ;; nothing to create the page with. MEASURED that day: a
              ;; single-word target rendered its chip and a multi-word one
              ;; rendered `[x](wikilink:x)` as prose.
              ;;
              ;; That is the same leak T-web-md-004 was written for, from the
              ;; other end. It is also half of the report that "clicking a new
              ;; wikilink does not create a note" — a person writing prose
              ;; writes `[[Some New Idea]]`, and 4 of the corpus's own targets
              ;; are already multi-word (`Alatus Help`, `bidirectional links`,
              ;; `dual process`, `Dual-Process Thinking`).
              ;;
              ;; encodeURIComponent RATHER THAN ANGLE BRACKETS. `(<dest>)` is
              ;; the other legal form and it fails on a title holding `<` or
              ;; `>` — "Projects > IKC3" is George's own example of how he
              ;; names a hierarchy. Percent-encoding has no such character.
              ;;
              ;; :delta NEEDS NO ADJUSTMENT. It is computed from
              ;; `(count replacement)` below, so a longer destination is
              ;; already accounted for and a highlight below a wikilink stays
              ;; level with its own words.
              replacement (str "[" (or (some-> label str/trim) (str/trim target)) "]"
                               "(wikilink:" (js/encodeURIComponent (str/trim target)) ")")
              out'        (str out (subs s last-end start) replacement)]
          (recur (+ start (count matched))
                 out'
                 ;; Recorded in ORIGINAL coordinates: everything at or after
                 ;; :orig-end was pushed along by :delta. Original coordinates
                 ;; because that is what a stored highlight is in, and converting
                 ;; one way is enough.
                 (conj edits {:orig-end (+ start (count matched))
                              :delta (- (count replacement) (count matched))})))
        [(str out (subs s last-end)) edits]))))

(defn- to-rewritten
  "An offset in the ORIGINAL text → the same place in the rewritten one.

   Every edit that ends at or before this position has already shifted it, so the
   answer is the position plus the deltas of the edits above it. Positions inside
   a rewritten wikilink are not meaningful and do not arise: a highlight's range
   comes from the source, and a reader cannot select the `(wikilink:…)` that only
   exists after the rewrite."
  [pos edits]
  (reduce (fn [p {:keys [orig-end delta]}]
            (if (>= pos orig-end) (+ p delta) p))
          pos
          edits))

(defn- shift-highlights
  "Highlights, with their offsets moved into the rewritten string's coordinates."
  [highlights edits]
  (if (empty? edits)
    (vec highlights)
    (mapv (fn [h]
            (-> h
                (update :start #(when % (to-rewritten % edits)))
                (update :end   #(when % (to-rewritten % edits)))))
          highlights)))

;; -----------------------------------------------------------------------------
;; Inline token → hiccup pieces.
;; -----------------------------------------------------------------------------

(defn- escape-for-raw-html
  "Escape < > & for the ONE place that injects a string as raw HTML.

   IT USED TO BE CALLED ON EVERY PIECE OF TEXT, and that was the bug George
   reported on 2026-09-07: `a > b` reached the screen as the literal characters
   `a &gt; b`, and NO `->` anywhere in the corpus was drawn as an arrow.

   ITS OLD DOCSTRING NAMED THE WRONG PREMISE: \"Marked returns some raw text
   unescaped; escape < > & for safe hiccup.\" Both halves mislead. Marked returns
   text unescaped, yes — measured 2026-09-08 through the app's own
   `(lexer src #js {:gfm true})`, for paragraph, heading and blockquote alike.
   But hiccup is not HTML: Reagent hands a string child to React, and React
   escapes it. So escaping first produced `&amp;gt;`, which the browser then
   renders as the four characters `&gt;`.

   A STRING CHILD NEEDS NOTHING. `dangerouslySetInnerHTML` needs this, because
   that IS raw HTML and the point there is to SHOW embedded markup rather than
   run it. That one caller is why the function survives, and its name now says
   so."
  [s]
  (-> (str s)
      (str/replace "&" "&amp;")
      (str/replace "<" "&lt;")
      (str/replace ">" "&gt;")))

(def ^:dynamic *on-tag*
  "What a #hashtag chip does when it is clicked, or nil.

   A DYNAMIC VAR AND NOT A THREADED ARGUMENT, because a chip is made four levels
   below `render` — through the token walk, through a container's children, and
   through inline->pieces — and every one of those levels would have to carry a
   value it has no interest in. The binding is active while the hiccup is BUILT,
   and the click closure captures it there, so nothing depends on it at click
   time.

   nil KEEPS THE CURRENT BEHAVIOUR. A chip with no handler is inert, which is
   what every surface drew before the tag filter existed."
  nil)

(def ^:dynamic *refused*
  "The set of lowercased tag paths a person has REFUSED, or nil.

   A PERSON KNOWS WHAT A RULE CANNOT. Five refusals are in the grammar and
   every one of them arrived from this corpus, so the next pasted file will
   find a sixth. The grammar keeps what is true of all text, and a person’s own
   refusal is data (ADR-ikc-v3-101-01).

   A REFUSED WORD DRAWS AS PLAIN TEXT. It must, or the reader goes on clicking
   a chip that leads to a tag with no mentions behind it — which is exactly
   what the two IntelliJ logs did before B3.

   A DYNAMIC VAR AND NOT AN ARGUMENT, for the reason *on-tag* above gives: a
   chip is made four levels below render. nil and the empty set both mean the
   same thing, so a surface that binds nothing behaves as it did."
  nil)

(defn- chip-class
  "Tailwind class for a #hashtag chip. A chip that can be clicked says so."
  [clickable?]
  (str "px-1 rounded border border-[var(--border)] "
       "bg-[var(--raised)] text-[var(--accent)] text-xs font-medium"
       (when clickable? " cursor-pointer hover:bg-[var(--gutter)]")))

(def hashtag-re-source
  "ikc1's own hashtag pattern, plus the slash that makes a path
   (ADR-ikc-v3-080-01 decision 1), and plus the LEAD-IN the renderer never had.

   `#a/b` IS ONE CHIP. Without the slash the renderer drew `#a` as a chip and
   `/b` as plain text, so a hierarchical tag looked like two things.

   THE LEAD-IN IS A REAL FIX, not a transcription. This renderer matched a `#`
   anywhere, so `C#` drew a chip reading `#` and a URL fragment drew one too. The
   server-side grammar has required a boundary since ikc1, and the two disagreed
   — the same text was a tag on one side and not on the other.

   THIS STRING IS DECLARED. dsl/contexts/tags.edn :levels :grammar :pattern
   holds it, and validate-dsl compares the VALUE of this literal and of the
   regex literal in src/tags/hashtags.clj against it. Group 1 is the lead-in
   and group 2 is the tag."
  "(^|[\\s,;(])#([A-Za-z][A-Za-z0-9_/-]*)")

(defn- hex-colour?
  "Is this tag text a CSS colour rather than a word?

   tags.hashtags/hex-colour? in the browser, rule for rule. A token is a colour
   when it is hex-shaped AND it carries a decimal digit, or it is six or eight
   characters long. `#fff` and `#dead` stay words. `#decade` is refused, and
   dsl/contexts/tags.edn names that cost."
  [raw]
  (boolean
    (and (re-matches #"[0-9a-fA-F]+" raw)
         (#{3 4 6 8} (count raw))
         (or (re-find #"[0-9]" raw)
             (#{6 8} (count raw))))))

(defn- anchor?
  "Is the `#` at index i the start of a markdown anchor, as in `[x](#y)`?
   `(` is a legal lead-in, so the pattern matches one."
  [text i]
  (and (>= i 2) (= "](" (subs text (- i 2) i))))

(defn- continues-after-dot?
  "Does the token GO ON after the dot that ends the match?

   `.` is not a tag character, so the match stops at the first one and
   `#c.i.u.SlowOperations` captured `c`. A letter or a digit after the dot
   means the token continues. The end of the line means a full stop, and
   `#parent/child.` keeps its chip."
  [text end]
  (and (< (inc end) (count text))
       (= "." (subs text end (inc end)))
       (re-matches #"[A-Za-z0-9]" (subs text (inc end) (+ end 2)))))

(defn- clean
  "The tag text with a trailing separator removed, or nil when the token is
   refused. tags.hashtags/clean in the browser.

   A SINGLE CHARACTER IS NOT A TAG. George: `If I want tag for C I will name
   it C_lang`. An empty segment — `#aa//bb` — refuses the whole token, because
   a guess about what was meant would invent a tag."
  [raw]
  (let [t (str/replace (or raw "") #"[/-]+$" "")]
    (when (and (> (count t) 1)
               (not (str/includes? t "//"))
               (not (hex-colour? t)))
      t)))

(defn scan
  "One string of plain text, as [{:kind :text :s} … {:kind :tag :tag :s} …].

   THE BROWSER'S HALF OF THE HASHTAG GRAMMAR, and the only one. Both
   text-with-hashtags and tags-in read this, so what the reader clicks and what
   the conformance table asserts can never be two different answers.

   THE GRAMMAR IS DECLARED, in dsl/contexts/tags.edn :levels :grammar. The
   pattern above, the four refusals below and the trailing-separator cleanup
   are all stated there, and :cases is the table src/tags/hashtags.clj answers
   too (PLAN-ikc-v3-032-01 B2).

   NO CHARACTER IS LOST. A refused token becomes plain text exactly as it is
   written. A cleaned one gives the chip its tag and leaves the separator it
   dropped in the text piece after it."
  [s]
  (let [text (str s)
        re   (js/RegExp. hashtag-re-source "gm")]
    (loop [last-end 0 out []]
      (if-let [m (.exec re text)]
        (let [start   (.-index m)
              lead    (or (aget m 1) "")
              raw     (aget m 2)
              ;; the chip begins AFTER the lead-in, which stays plain text
              chip-at (+ start (count lead))
              end     (+ start (count (aget m 0)))
              tag     (when-not (or (anchor? text chip-at)
                                    (continues-after-dot? text end))
                        (let [t (clean raw)]
                          ;; A PERSON’S REFUSAL, applied last. The grammar has
                          ;; already said the token is well formed. This is the
                          ;; one rule no pattern could carry.
                          (when-not (and t (contains? (or *refused* #{})
                                                      (str/lower-case t)))
                            t)))
              tag-end (when tag (+ chip-at 1 (count tag)))
              out     (cond-> out
                        (< last-end chip-at)
                        (conj {:kind :text :s (subs text last-end chip-at)}))]
          (recur end
                 (if tag
                   (cond-> (conj out {:kind :tag :tag tag
                                      :s (subs text chip-at tag-end)})
                     (< tag-end end)
                     (conj {:kind :text :s (subs text tag-end end)}))
                   (conj out {:kind :text :s (subs text chip-at end)}))))
        (cond-> out
          (< last-end (count text))
          (conj {:kind :text :s (subs text last-end)}))))))

(defn tags-in
  "The tag texts of one string of plain text, in order.

   THE PUBLIC HALF OF THE GRAMMAR, and it exists so the conformance table can
   ask the renderer the same question it asks tags.hashtags/mentions. It reads
   the same scan the chips are drawn from."
  [s]
  (into [] (comp (filter #(= :tag (:kind %))) (map :tag)) (scan s)))

(defn- text-with-hashtags
  "Split plain text into string pieces and #hashtag chips."
  [s]
  (let [on *on-tag*]
    (mapv (fn [{:keys [kind tag] piece :s}]
            (if (= :tag kind)
              [:span (cond-> {:class (chip-class (some? on))}
                       on (assoc :on-click
                                 (fn [e]
                                   (.preventDefault e)
                                   (.stopPropagation e)
                                   (on tag))))
               piece]
              piece))
          (scan s))))

(declare inline->pieces)

;; -----------------------------------------------------------------------------
;; Highlight paint (F-platform-v0-011-01, ADR-graph-ui-v0-017-01 §2-3)
;;
;; PAINTED BY OFFSET. Every rendered text run knows which slice of the SOURCE
;; produced it, so a highlight's stored [start,end) is placed by intersection
;; arithmetic — no searching for the text on screen.
;;
;; IT WAS PAINTED BY TEXT MATCH UNTIL 2026-08-15, and that failed on most real
;; selections, which is worth recording because the reasoning sounded good:
;; :anchor-text is authoritative for FINDING a highlight when the text moves, so
;; matching it seemed like asking the same question. It is not. Markdown's whole
;; job is that the rendered string differs from its source — `# Pi Mono` renders
;; without the `#`, `**bold**` without the asterisks, `[[Page]]` as a label — so
;; the search failed for every heading, list marker and emphasis. Measured on a
;; real note: the margin listed the highlight and the row showed no wash at all.
;;
;; anchor-text still decides WHERE a highlight is when the document changes
;; (CONCEPT-ikc-v3-008-01, unchanged). Offsets decide where it is DRAWN, which is
;; what they were always for.
;;
;; A highlight spanning several runs now paints as several marks, one per run —
;; impossible under text matching, and the ordinary case for any selection that
;; crosses emphasis.
;;
;; THE RAWS WERE TRUSTED TO TILE until 2026-08-19, and marked promises that only
;; for TOP-LEVEL tokens. A container's children carry DEDENTED raws: a list
;; item's continuation lines lose their indent, a blockquote's lines lose their
;; `> `. Two symptoms, both found by George on a real GAKS plan. Every sibling
;; after the first in a container painted at the FIRST sibling's base, so one
;; highlight washed three list items. And inside a wrapped list item each line
;; drifted left by the indent width, so the wash started mid-word. The cure is
;; `align` below: each LINE of a dedented raw is still verbatim source, so a
;; forward index-of from a running cursor recovers every true position — the
;; same recorded-edit idea `wikify` already applies to wikilinks.
;; -----------------------------------------------------------------------------

(defn- first-line
  "Everything before the first newline. The one slice of a token's raw that is
   always verbatim source, whatever marked dedented from the lines below it."
  [s]
  (let [s (or s "")
        i (str/index-of s "\n")]
    (if i (subs s 0 i) s)))

(defn- align
  "The true source position of `raw`, found by its first line at or after `from`.

   marked dedents a container's children — see the section comment above — so a
   child's raw is NOT a substring of the source, and a raw-length cursor drifts
   by the accumulated indents. The raw's first LINE is verbatim, and siblings sit
   in source order, so the first match from a conservative cursor is the real
   one. A failed search keeps the cursor: wrong-by-less, and never thrown.

   `wsrc` nil means nothing will be painted — render passes the source only when
   highlights exist — so the cursor is returned unsearched."
  [wsrc from raw]
  (let [line (first-line raw)]
    (if (or (nil? wsrc) (str/blank? line))
      from
      (or (str/index-of wsrc line from) from))))

(defn highlight-colour
  "The CSS value of a highlight's colour: its theme token, var(--hl-1) to
   var(--hl-4). The gutter dot and the wash over the words both read it here,
   so the two cannot name different colours."
  [color]
  (str "var(--" (name (or color :hl-1)) ")"))

(defn highlight-wash
  "The background of a mark: the theme token at 35%, mixed rather than a fixed
   rgba, so overlapping marks composite and darken and a theme change moves
   them."
  [color]
  (str "color-mix(in srgb, " (highlight-colour color) " 35%, transparent)"))

(defn- mark-class
  "The classes of a mark. Each one is a literal in this file, so the build of
   tailwind.css finds it.

   THE COLOUR IS NOT A CLASS (fault report d122f7ab, 2026-10-03). It was a
   Tailwind class put together at run time, one per colour. tailwind.css is
   built from a scan of the source, and a scan cannot find a class that does
   not exist until run time. So the wash had no rule, and every mark drew in
   the browser's own mark yellow while the gutter dot was right. The colour
   is now an inline style, as the gutter dot's is (`nest-marks`)."
  ([] (mark-class nil))
  ([level]
   (str "rounded-sm px-[1px]"
        ;; THE LAYER RIDES ON THE WASH (RESEARCH-ikc-v3-021-01 D5, PLAN-026 D):
        ;; layer 1 is the wash alone; 2 to 5 add weight and underline through
        ;; one class per layer in ikc-base.css, so a layer-3 span inside a
        ;; layer-2 span is bold and washed twice. The colour stays the reader's.
        (when (and (int? level) (<= 2 level)) (str " ikc-hl-layer-" (min level 5))))))

(defn- covering
  "The highlights overlapping the source range [base, base+len), as local
   [from,to) offsets inside the run, longest first so a word marked inside a
   marked sentence nests rather than replacing it."
  [highlights base len]
  (->> highlights
       (keep (fn [{:keys [id start end color orphaned n kind level]}]
               (when (and (not orphaned) (int? start) (int? end) (< start end))
                 (let [from (max 0 (- start base))
                       to   (min len (- end base))]
                   ;; :n RIDES WITH THE MARK — the footnote number
                   ;; (ui.highlights-web/numbered). It is derived from reading
                   ;; order on the server, so the renderer needs no second lookup
                   ;; and cannot disagree with the list.
                   (when (< from to)
                     {:from from :to to :color color :id id :n n :kind kind :level level})))))
       (sort-by (fn [{:keys [from to]}] [(- from to) from]))
       vec))

(defn segments
  "The run cut at EVERY highlight boundary, each piece carrying the highlights
   that cover it, longest first.

   This is what makes overlap work. `hits` are local `{:from :to}` ranges; cutting
   at every boundary means a piece is covered by a SET of highlights rather than
   by one, so a word marked inside a marked sentence is one piece under both — and
   two highlights that merely CROSS, sharing a middle without either containing
   the other, are handled by the same rule. George asked for exactly that on
   2026-08-13: \"do not infer that highlights should be concentric — they may be
   cross boundaries.\"

   PUBLIC because it is the whole of the overlap logic and testable without a
   browser."
  [hits len]
  (let [bounds (->> (mapcat (fn [{:keys [from to]}] [from to]) hits)
                    (concat [0 len])
                    (filter #(and (<= 0 %) (<= % len)))
                    distinct sort vec)]
    (vec (for [[a b] (partition 2 1 bounds)
               :when (< a b)]
           {:from a :to b
            ;; `hits` arrives longest-first from `covering`, and filter keeps that
            ;; order — so the widest highlight ends up the OUTERMOST mark.
            :hls (vec (filter (fn [{:keys [from to]}] (and (<= from a) (<= b to))) hits))}))))

(defn- scroll-to-row!
  "Bring the footnote's own ROW into view, and flash it.

   George, 2026-08-24, on what a number is for: a click on it should carry a
   reader between the words and the note. This is the first half — the number is
   in the text, the note is in the list below, and the list can be long.

   THE ROW CARRIES ITS VALUE as `data-row-value` (widget-tree), which is the
   mark's id — the same id the number carries. So this is a lookup, not a search.

   IT LIVES HERE AND NOT IN widgets.cljs, where its twin scroll-to-highlight!
   sits, because widgets requires THIS namespace: the other direction would be a
   cycle. Ten lines duplicated is the smaller cost, and the two are named for
   each other."
  [id]
  (when id
    (when-let [el (.querySelector js/document
                                  (str "[data-row-value=\"" id "\"]"))]
      (.scrollIntoView el #js {:behavior "smooth" :block "center"})
      (.add (.-classList el) "ring-2")
      (js/setTimeout #(.remove (.-classList el) "ring-2") 1200))))

(defn- nest-marks
  "A piece wrapped in one <mark> per covering highlight, longest OUTERMOST.
   Wrap shortest-first so the LAST wrap — the longest highlight — ends up the
   outer element (`hls` arrives longest-first). Every mark carries its own id,
   so the gutter beside it can scroll to the words whichever layer the reader
   means (George, 2026-08-15)."
  [piece hls]
  (reduce (fn [inner {:keys [color id level]}]
            [:mark {:class (mark-class level)
                    ;; THE COLOUR IN THE STYLE, where no build can lose it.
                    ;; The browser's own mark sets a yellow background-color
                    ;; and a black text colour. Both are replaced here, so the
                    ;; words keep the theme's text colour on a dark theme.
                    :style {:backgroundColor (highlight-wash color)
                            :color "inherit"}
                    :data-highlight-id id}
             inner])
          piece
          (reverse hls)))

(defn- bridging
  "The highlights whose range overlaps the source gap [from, to) — a newline
   plus the indent marked dedented away. A wash covering the last word of one
   line and the first word of the next must also cover the space the reader
   sees between them: unwashed, the one highlight shows as two (George,
   2026-08-19: \"the first highlight is interrupted\"). Longest first, the same
   nesting order `covering` gives."
  [highlights from to]
  (->> highlights
       (filter (fn [{:keys [start end orphaned]}]
                 (and (not orphaned) (int? start) (int? end)
                      (< start to) (> end from))))
       (sort-by (fn [{:keys [start end]}] (- start end)))
       vec))

(defn- paint-line
  "One rendered text LINE, split at the highlights that cover its source range.

   `base` is where this line begins in the (wikified) source; `text` is what the
   reader sees. Within a line the two have the same length — dedenting only
   happens at newlines, which is why paint-run cuts there before calling this —
   so an offset in one is an offset in the other, which is the entire trick.

   OVERLAPPING MARKS NEST, they do not compete. Until 2026-08-16 this walked the
   hits longest-first and SKIPPED any that started inside one already painted, so
   a word marked inside a marked sentence drew nothing, and two crossing
   highlights drew one. George: \"it does not render overlapping highlights.\" The
   wash is `color-mix(… 35%, transparent)` precisely so nested marks composite and
   darken — the layering BASB is for — and nothing was ever nested to composite."
  [text base highlights]
  (let [hits (covering highlights base (count text))]
    (if (empty? hits)
      (text-with-hashtags text)
      (vec (mapcat
            (fn [{:keys [from to hls]}]
              (let [piece (subs text from to)]
                (if (empty? hls)
                  (text-with-hashtags piece)
                  ;; THE NUMBER GOES WHERE THE MARK ENDS. George, 2026-08-24:
                  ;; "let's instead of post-it build numbered footnotes which will
                  ;; provide better handling". A footnote is a highlight with a
                  ;; note, so the marker is drawn after the last piece of that
                  ;; mark — which is what a footnote marker means in print.
                  ;; A BOOKMARK PAINTS NO WASH, and it must still be reachable.
                  ;; George, 2026-08-24: it marks a place he means to come back
                  ;; to, and the way back is a click in the list — which scrolls
                  ;; to the element carrying this id. So a bookmark draws a small
                  ;; glyph at its START instead of colouring the words.
                  (let [washes (vec (remove #(= :bookmark (:kind %)) hls))
                        marks  (filter #(and (= :bookmark (:kind %))
                                             (= from (:from %)))
                                       hls)]
                    (into (vec (for [{:keys [id]} marks]
                                 [:span {:class "text-[var(--accent)] mr-0.5 select-none"
                                         :data-highlight-id id
                                         :title "a bookmarked place"}
                                  "🔖"]))
                          (cons (if (seq washes)
                                  (nest-marks piece washes)
                                  piece)
                                (for [{:keys [n id] :as hl} hls
                                      :when (and n (= to (:to hl)))]
                                  [:sup {:class (str "ml-0.5 text-[var(--accent)] font-semibold "
                                                     "cursor-pointer hover:underline")
                                         :data-footnote-id id
                                         :data-footnote n
                                         :title "go to this footnote"
                                         :on-click (fn [e]
                                                     (.stopPropagation e)
                                                     (scroll-to-row! id))}
                                   (str n)])))))))
            (segments hits (count text)))))))

(defn- paint-run
  "One rendered text run, painted line by line.

   A run's text matches its source length only WITHIN a line: marked dedents
   continuation lines, so after each newline the source may hold an indent (or a
   `> `) the text does not. So each line is re-aligned before painting — line k's
   wash lands where line k really is, not where line 0's arithmetic puts it. The
   emitted \"\\n\" keeps the text intact under whitespace-pre-wrap, and is itself
   washed when a highlight spans it, so a wrapped wash reads as ONE mark."
  [text base highlights wsrc]
  (let [lines (str/split text #"\n" -1)]
    (if (= 1 (count lines))
      (paint-line text base highlights)
      (loop [ls (seq lines), cursor base, first? true, out []]
        (if ls
          (let [line  (first ls)
                start (if first? cursor (align wsrc cursor line))
                sep   (when-not first?
                        (if-let [hls (and (< cursor start)
                                          (seq (bridging highlights cursor start)))]
                          (nest-marks "\n" (vec hls))
                          "\n"))]
            (recur (next ls)
                   (+ start (count line))
                   false
                   (-> (cond-> out sep (conj sep))
                       (into (paint-line line start highlights)))))
          out)))))

(def ^:private code-path-re
  "A repository path written as code: two or more segments and an extension,
   `dsl/contexts/workflow.edn`. THE SAME SHAPE AS ui.notes-web/code-path-re,
   which resolves it. The two must agree, or a resolved path draws as code."
  #"^[A-Za-z0-9_.-]+(?:/[A-Za-z0-9_.-]+)+\.[A-Za-z0-9]+$")

(defn- code-path?
  "Is the text of a code span a repository path?"
  [s]
  (boolean (re-matches code-path-re (str s))))

(defn- token->inline-piece
  "Turn one marked inline token into a hiccup piece (or a list of pieces).

   `base` is where this token's raw begins in the (wikified) source, so a text run
   can be painted by offset. Children are given the container's own base and
   inline->pieces aligns each of them past the container's syntax — the `**`, the
   `# `, the `[` — by searching for it, so no syntax length is hardcoded."
  [^js tok link-target on-nav highlights base wsrc]
  (let [type (.-type tok)
        text (or (.-text tok) (.-raw tok) "")]
    (case (keyword type)
      :text       (into [:<>] (if (seq highlights)
                                (paint-run text base highlights wsrc)
                                (text-with-hashtags text)))
      ;; A PATH WRITTEN AS CODE OPENS ITS FILE (fault report fb6ea567, George,
      ;; 2026-10-07: "The link dsl/contexts/workflow.edn on the first line is
      ;; not working"). The code span was drawn in the accent colour, the colour
      ;; of a link chip, and it had no click. So a person read it as a link
      ;; that did nothing.
      ;;
      ;; NOW THERE ARE TWO CASES, AND THEY LOOK DIFFERENT. A path that
      ;; :page-content-links resolves (the server's code-path-re) is a link to
      ;; the page of its file. Any other code is code, in the text colour,
      ;; with no click. Only a path shape is asked: code that happens to be a
      ;; page's title is not a link.
      :codespan   (let [code (.-text tok)
                        pid  (when (and link-target on-nav (code-path? code))
                               (link-target code))]
                    (if pid
                      [:code {:class (str "px-1 rounded bg-[var(--raised)] text-[var(--accent)] "
                                          "text-[0.85em] underline cursor-pointer ikc-hover")
                              :role "link"
                              :title (str "Open " code)
                              :on-click (fn [e]
                                          ;; NOT A CLICK ON THE ROW: an outline row
                                          ;; opens its editor on a click.
                                          (when e (.preventDefault e) (.stopPropagation e))
                                          (on-nav pid code))}
                       code]
                      [:code {:class "px-1 rounded bg-[var(--raised)] text-[var(--text)] text-[0.85em]"}
                       code]))
      :strong     (into [:strong {}] (inline->pieces (or (.-tokens tok) [tok]) link-target on-nav highlights base wsrc))
      :em         (into [:em {}] (inline->pieces (or (.-tokens tok) [tok]) link-target on-nav highlights base wsrc))
      :del        (into [:del {}] (inline->pieces (or (.-tokens tok) [tok]) link-target on-nav highlights base wsrc))
      :link       (let [href (.-href tok)
                        label-tokens (.-tokens tok)]
                    (if (str/starts-with? href "wikilink:")
                      (let [;; DECODED BACK, because wikify percent-encoded it
                            ;; so marked would accept a destination with
                            ;; spaces in it. Every consumer below wants the
                            ;; page's real title: `link-target` looks it up,
                            ;; the chip prints it, and the create command
                            ;; carries it.
                            ;;
                            ;; A MALFORMED SEQUENCE IS NOT A CRASH.
                            ;; decodeURIComponent throws on a lone `%`, which a
                            ;; page title may legitimately hold — and one bad
                            ;; title must not take the whole document's render
                            ;; with it. The raw form is the honest fall-back:
                            ;; it is what the link said.
                            target (try (js/decodeURIComponent
                                          (subs href (count "wikilink:")))
                                        (catch :default _
                                          (subs href (count "wikilink:"))))
                            pid    (when link-target (link-target target))]
                        ;; AN UNRESOLVED WIKILINK IS THE INVITATION TO CREATE
                        ;; THAT PAGE (George, 2026-09-07: *"Writing new wikilink
                        ;; and clicking on it does not create new note"*).
                        ;;
                        ;; The :markdown kind's own declaration has said so
                        ;; since it was written: "A target that names no page is
                        ;; still DRAWN, as a muted chip that does nothing. An
                        ;; unresolved wikilink is an invitation to create that
                        ;; page, not an error." The chip was drawn. The
                        ;; invitation was not — `(when (and pid on-nav) …)` left
                        ;; a target with no page WITHOUT a click handler at all,
                        ;; so naming a page in a rendered document was the one
                        ;; place in the reader where you could not make one.
                        ;;
                        ;; THE OTHER LINKIFIER HAD IT RIGHT, and that is what
                        ;; made this invisible. widgets.cljs' plain-text wikilink
                        ;; path already dispatches :ui/follow-wikilink for an
                        ;; unresolved target and styles it dashed — "a promise
                        ;; rather than a fact". Two renderers of one syntax, and
                        ;; only one of them read the declaration. The comment on
                        ;; the outline row below claims it uses "the same
                        ;; renderer … rather than a second, nearly-identical
                        ;; linkifier", which was true of the markdown and not of
                        ;; the wikilinks in it.
                        ;;
                        ;; SO THE CLASSES ARE THE OTHER PATH'S CLASSES, and the
                        ;; two chips now look alike wherever a page names one:
                        ;; solid and accented when the page is real, dashed and
                        ;; muted while it is only named.
                        [:span {:class (str "px-1 rounded cursor-pointer "
                                            (if pid
                                              (str "border border-[var(--border)] bg-[var(--raised)] "
                                                   "text-[var(--accent)] hover:bg-[var(--hover)] "
                                                   "hover:border-[var(--accent)]")
                                              (str "border border-dashed border-[var(--faint)] "
                                                   "text-[var(--muted)] hover:text-[var(--accent)] "
                                                   "hover:border-[var(--accent)]")))
                                :title (if pid (str "Open " target) (str "Create " target))
                                ;; THE TARGET GOES WITH THE PAGE ID (2026-08-24).
                                ;; A `mark:<id>` link must land on the WORDS, and
                                ;; only the target carries the id. The 1-arity is
                                ;; kept for every other caller.
                                ;;
                                ;; `pid` IS NOW ALLOWED TO BE nil, and the caller
                                ;; decides what that means: this file knows a
                                ;; target and whether it resolved, and nothing
                                ;; about which command answers either case.
                                ;;
                                ;; EXCEPT A `mark:` TARGET, WHICH IS NOT A PAGE
                                ;; NAME. It addresses a highlight inside a page
                                ;; that must already exist, so an unresolved one
                                ;; names a mark that has been edited away — and
                                ;; a page titled `mark:<uuid>` is what "create
                                ;; it" would otherwise mean. The rule is here
                                ;; and not in the two callers because it is a
                                ;; fact about the SYNTAX, and this file is the
                                ;; only one that reads it.
                                :on-click (when (and on-nav
                                                     (or pid
                                                         (not (str/starts-with? target "mark:"))))
                                            #(on-nav pid target))}
                         (if (seq label-tokens)
                           (into [:<>] (inline->pieces label-tokens link-target on-nav highlights base wsrc))
                           text)])
                      ;; A RELATIVE LINK TO A FILE THIS VAULT HOLDS STAYS IN THE
                      ;; APPLICATION. `[x](arch-root/index.md)` used to render as
                      ;; an ordinary external anchor, so clicking it opened a new
                      ;; tab at the static server, which serves index.html for any
                      ;; unknown path — and the app then booted with no ?island=
                      ;; and attached to `gi` (George, 2026-08-18, clicking the
                      ;; link in README.md; 28 such links are in the vault).
                      ;;
                      ;; :page-content-links resolves these on the server, keyed
                      ;; by the href AS WRITTEN, so the same link-target fn a
                      ;; wikilink uses answers for them.
                      ;;
                      ;; It stays an <a> rather than becoming a chip: it is an
                      ;; ordinary markdown link and should look like one, and
                      ;; keeping the href means the target still shows on hover.
                      ;; The click is intercepted; a middle-click still opens the
                      ;; file the href names, which is the honest fallback.
                      (let [pid (when link-target (link-target href))]
                        [:a (cond-> {:href href
                                     :class "text-[var(--accent)] underline ikc-hover"}
                              (and pid on-nav)
                              (assoc :on-click (fn [e] (.preventDefault e) (on-nav pid)))
                              (not (and pid on-nav))
                              (assoc :target "_blank" :rel "noreferrer"))
                         (if (seq label-tokens)
                           (into [:<>] (inline->pieces label-tokens link-target on-nav highlights base wsrc))
                           text)])))
      :html       [:span {:dangerouslySetInnerHTML {:__html (escape-for-raw-html text)}}]
      :br         [:br]
      ;; AN IMAGE IS DRAWN, NOT READ OUT AS ITS ALT TEXT (IKC3-Bugs-20260928
      ;; item 9). Until 2026-09-29 this token fell through to the fallback
      ;; below, so `![x](…)` showed the word x. A pasted image lives in the
      ;; :attachments vault, and graph-ui.web.note-image reads it through the
      ;; bus.
      :image      [note-image/note-image (.-href tok) (.-text tok)]
      :escape     text
      ;; fallback for any token we don't explicitly handle
      text)))

(defn inline->pieces
  "Parse a vector of marked inline tokens into hiccup pieces.

   Walks left to right with a cursor, ALIGNING every token to its true source
   start before rendering it. Within a line the raw lengths tile the source, so
   the search hits at the cursor itself. Where a dedented raw breaks the tiling,
   the raw-length advance undershoots — never overshoots, a dedented raw is only
   ever shorter than its source — and the next search recovers the loss."
  ([tokens link-target on-nav] (inline->pieces tokens link-target on-nav nil 0 nil))
  ([tokens link-target on-nav highlights] (inline->pieces tokens link-target on-nav highlights 0 nil))
  ([tokens link-target on-nav highlights base] (inline->pieces tokens link-target on-nav highlights base nil))
  ([tokens link-target on-nav highlights base wsrc]
   (loop [ts (seq tokens), cursor base, out []]
     (if-let [t (first ts)]
       (let [raw   (or (.-raw ^js t) (.-text ^js t) "")
             start (align wsrc cursor raw)]
         (recur (next ts)
                (+ start (count raw))
                (conj out (token->inline-piece t link-target on-nav highlights start wsrc))))
       out))))

;; -----------------------------------------------------------------------------
;; Block token → hiccup element.
;; -----------------------------------------------------------------------------

(defn- block-class
  "The wrapper class for one rendered block. Colour only.

   IT SET ITS OWN SIZE until 2026-08-19 — `text-sm leading-relaxed`, which is
   14px against the theme's own `--type-body-size` of 15px. George: \"Isn't it a
   theme property?\" It is. `type-body` is a declared composite token carrying
   size, weight and line-height, and this class overrode it everywhere markdown
   is drawn.

   It also made the type FLOOR unreachable. A block row computes a font-size
   that shrinks so the line keeps 45 characters, and a hardcoded size on the
   text inside it wins over any size inherited from the row. So the floor could
   not have moved this text even when its arithmetic was correct.

   The size now comes from the box that holds the markdown — `--type-body-size`
   on the body, or the floor expression on a block row's text. Both are the same
   theme property. One of them has a floor applied to it.

   `ikc-md` KEEPS A WIDE LINE IN ITS OWN BLOCK (PLAN-ikc-v3-054-01, lane PT).
   A run of characters with no place to break, as a border of box characters
   is, went past the right edge of the reading pane in Firefox. ikc-base.css
   gives `ikc-md` a horizontal scroll, so such a line scrolls inside the block
   it is in. The parent of a block's markdown is a flex item with `min-w-0`,
   so the scroll box moves no vertical margin."
  []
  "ikc-md text-[var(--text)]")

(def ^:private table-class
  "The class of a <table>: a markdown table with `|`.

   NO FONT SIZE SINCE 2026-10-05, for the reason box-table-class below gives,
   met a second time. George asked for A− and A+ in the reader while he edited
   the SSOT, which is mostly tables. A table at a fixed `text-sm` stayed 14 px
   whatever size he chose. A table now takes the size of its text."
  "border-collapse text-[var(--text)] w-full")

(def ^:private box-table-class
  "The class of a console table. The same as `table-class` with no font size.

   A CONSOLE TABLE TAKES THE SIZE OF ITS BLOCK. `text-sm` is a fixed size, and
   a fixed size beats the type floor of a block row (block-class above says
   why). Measured 2026-09-29 on a probe bus: in a block 80 px wide, the cells
   drew at 14 px beside text at 6 px, and broke each word letter by letter."
  "border-collapse text-[var(--text)] w-full")

(def ^:private th-class
  "border border-[var(--border)] px-2 py-1 text-left font-semibold text-[var(--heading)]")

(def ^:private td-class
  "border border-[var(--border)] px-2 py-1 align-top")

(declare block-token->hiccup)

(defn ordered-gutter
  "PURE. The left space an ordered list needs, as a CSS length: the widest
   number, its full stop, and half a character of air. `start` is marked's
   `.start` — a number, or \"\" when the list starts at 1. Measured in `ch`,
   the width of one digit, so it follows the font and the zoom."
  [start n]
  (let [first-n (if (number? start) start 1)
        last-n  (+ first-n (max 0 (dec (or n 0))))
        digits  (count (str (max 1 last-n)))]
    (str (+ digits 1.5) "ch")))

(defn- block-children
  "A container's child tokens rendered in order, each at its own ALIGNED source
   start. Until 2026-08-19 every child was given the base of the FIRST one — no
   cursor at all — so the siblings claimed overlapping source ranges and one
   highlight painted in each of them. The cursor advances by each child's raw
   length, an undercount when the raw is dedented, which is safe: the next
   sibling is found by search from the cursor, never placed by it."
  [children link-target on-nav highlights base wsrc]
  (loop [ts (seq children), cursor base, out []]
    (if-let [t (first ts)]
      (let [raw   (or (.-raw ^js t) "")
            start (align wsrc cursor raw)]
        (recur (next ts)
               (+ start (count raw))
               (if-let [node (block-token->hiccup t link-target on-nav highlights start wsrc)]
                 (conj out node)
                 out)))
      out)))

(defn- block-token->hiccup
  "Turn one marked block token into a hiccup element.

   `base` is where this token's raw begins in the (wikified) source. Children
   are handed this same base; alignment inside inline->pieces and block-children
   moves each one past the container's own syntax."
  [^js tok link-target on-nav highlights base wsrc]
  (let [type (.-type tok)]
    (case (keyword type)
      :space   nil
      :paragraph
      (into [:p {:class "my-1 whitespace-pre-wrap"}]
            (inline->pieces (or (.-tokens tok) [tok]) link-target on-nav highlights base wsrc))

      ;; A BLOCK-LEVEL :text TOKEN, which is what every list item contains.
      ;;
      ;; marked gives a list_item one child of type "text", and that child holds
      ;; the INLINE tokens — the link, the strong, the codespan. Without this case
      ;; the token fell to the fallback at the bottom of this `case`, which prints
      ;; (.-text tok) through the escaper. So every list item rendered as raw
      ;; markdown, and a [[wikilink]] showed as the internal rewrite that only
      ;; ever exists between wikify and the lexer: `[label](wikilink:target)`.
      ;;
      ;; George found it on a GAKS document (2026-08-18), which is where it hurts
      ;; most: every corpus document keeps its Related list — the way one document
      ;; reaches another — as a bulleted list, so the whole corpus's navigation
      ;; was dead in the reading pane. It was never only about wikilinks; bold,
      ;; code spans and ordinary links in a list were raw too.
      ;;
      ;; Same shape as :paragraph, minus the <p>: walk the inline tokens, and fall
      ;; back to the token itself when it has none so highlight painting still
      ;; runs over plain text.
      :text
      (into [:<>]
            (inline->pieces (or (.-tokens tok) [tok]) link-target on-nav highlights base wsrc))

      :heading
      (let [lvl (.-depth tok)
            tag (keyword (str "h" lvl))
            ;; RELATIVE TO THE TEXT (2026-10-05). These were text-xl, text-lg,
            ;; text-base and text-sm — 20, 18, 16 and 14 px whatever the text
            ;; around them was. With A+ at its largest step the body is larger
            ;; than a fixed h1. The ratios keep those four sizes where the text
            ;; is type-body, 15 px.
            cls (case lvl
                  1 "text-[1.3333em] font-bold mt-3 mb-1 text-[var(--heading)]"
                  2 "text-[1.2em] font-bold mt-3 mb-1 text-[var(--heading)]"
                  3 "text-[1.0667em] font-semibold mt-2 mb-1 text-[var(--heading)]"
                  "text-[0.9333em] font-semibold mt-2 mb-1 text-[var(--heading)]")]
        (into [tag {:class cls}]
              (inline->pieces (.-tokens tok) link-target on-nav highlights base wsrc)))

      :code
      [:pre {:class "my-2 p-2 rounded bg-[var(--raised)] overflow-x-auto text-xs font-mono text-[var(--text)]"}
       [:code (.-text tok)]]

      :blockquote
      (into [:blockquote {:class "border-l-2 border-[var(--accent)] pl-3 my-2 italic text-[var(--muted)]"}]
            (block-children (.-tokens tok) link-target on-nav highlights base wsrc))

      :list
      ;; AN ORDERED LIST'S GUTTER IS AS WIDE AS ITS WIDEST NUMBER (George,
      ;; 2026-09-28: "after 99 show 00, not 100"). The marker sits OUTSIDE the
      ;; item, in the list's own left space. That space was a fixed 1.25rem —
      ;; room for "99." and not for "100." — so the scroll box around the
      ;; markdown cut the first digit off every number from 100 on.
      ;;
      ;; AN ORDERED LIST KEEPS ITS FIRST NUMBER (IKC-308, George, 2026-10-26:
      ;; "When I put the next number, it gets replaced with 1"). Each block of
      ;; an outline is its own list. So a block "2. text" is a list that starts
      ;; at 2. The <ol> had no start attribute, and the browser counted from 1.
      (if (.-ordered tok)
        (into [:ol (cond-> {:class "my-1 list-decimal tabular-nums"
                            :style {:paddingInlineStart
                                    (ordered-gutter (.-start tok) (count (.-items tok)))}}
                     (and (number? (.-start tok)) (not= 1 (.-start tok)))
                     (assoc :start (.-start tok)))]
              (block-children (.-items tok) link-target on-nav highlights base wsrc))
        (into [:ul {:class "ml-5 my-1 list-disc"}]
              (block-children (.-items tok) link-target on-nav highlights base wsrc)))

      :list_item
      (into [:li {:class "my-0.5"}]
            (block-children (.-tokens tok) link-target on-nav highlights base wsrc))

      :hr
      [:hr {:class "my-3 border-[var(--border)]"}]

      :table
      ;; Cells thread one cursor left-to-right, top-to-bottom, so a highlight in
      ;; a table paints only its own cell — the same sibling rule as a list. A
      ;; cell with an escaped pipe fails its search and keeps the cursor, which
      ;; is the pre-2026-08-19 placement, not a crash.
      (let [cell (fn [cursor ^js c tag cls]
                   (let [ctext (or (.-text c) "")
                         start (align wsrc cursor ctext)]
                     [(+ start (count ctext))
                      (into [tag {:class cls}]
                            (inline->pieces (or (.-tokens c) [c]) link-target on-nav highlights start wsrc))]))
            th-cls th-class
            td-cls td-class
            [cursor header-cells]
            (reduce (fn [[cur out] c]
                      (let [[cur' el] (cell cur c :th th-cls)]
                        [cur' (conj out el)]))
                    [base []] (.-header tok))
            [_ body-rows]
            (reduce (fn [[cur out] row]
                      (let [[cur' cells]
                            (reduce (fn [[cu cs] c]
                                      (let [[cu' el] (cell cu c :td td-cls)]
                                        [cu' (conj cs el)]))
                                    [cur []] row)]
                        [cur' (conj out (into [:tr] cells))]))
                    [cursor []] (.-rows tok))]
        [:div {:class "overflow-x-auto my-2"}
         (into [:table {:class table-class}]
               [(into [:thead] header-cells)
                (into [:tbody] body-rows)])])

      ;; fallback: raw text
      [:p {:class "my-1"} (or (.-text tok) (.-raw tok) "")])))

;; -----------------------------------------------------------------------------
;; A console table: the box characters of Claude Code, drawn as a <table>
;; (PLAN-ikc-v3-054-01, lane PT).
;;
;; George, 2026-09-29, on a table he pasted from the console into a block:
;; "lines extend beyond reading pane border". He asked for "a reflow based on
;; the content and typical width of a reading pane".
;;
;; THE RENDERER DERIVES THE TABLE. THE TEXT STAYS AS IT WAS PASTED. So a block
;; pasted before this rule draws as a table too, and a person who copies the
;; block gets back what the console printed.
;; -----------------------------------------------------------------------------

(def ^:private box-borders
  "The three border lines of a console table. For each first character: the
   junction between two columns, and the last character."
  {"┌" {:kind :top    :junction "┬" :end "┐"}
   "├" {:kind :rule   :junction "┼" :end "┤"}
   "└" {:kind :bottom :junction "┴" :end "┘"}})

(defn- box-line
  "One line, read as a part of a console table, or nil.

   A border line gives `{:kind :top|:rule|:bottom :widths [n …]}`: the width of
   each column, in `─` characters. A content line gives `{:kind :cells :cells
   [s …]}`. Spaces before and after the line do not count, because the console
   indents its answer and a copy can start at the first box character."
  [line]
  (let [s (str/trim line)
        n (count s)]
    (when (>= n 3)
      (let [head  (subs s 0 1)
            tail  (subs s (dec n))
            inner (subs s 1 (dec n))]
        (if-let [{:keys [kind junction end]} (get box-borders head)]
          (when (= tail end)
            (let [segs (vec (.split inner junction))]
              (when (every? #(re-matches #"─+" %) segs)
                {:kind kind :widths (mapv count segs)})))
          (when (and (= head "│") (= tail "│"))
            {:kind :cells :cells (vec (.split inner "│"))}))))))

(defn- table-at
  "The console table whose top border is line `i`, as `{:end j :groups [...]}`,
   or nil. `j` is the line of the bottom border. Each group is the content
   lines of one row, as vectors of cell pieces."
  [parsed i]
  (let [{:keys [kind widths]} (nth parsed i)
        ncol (count widths)]
    (when (and (= :top kind) (>= ncol 2))
      (loop [j (inc i), groups [], cur []]
        (when (< j (count parsed))
          (let [p (nth parsed j)]
            (cond
              (and (= :cells (:kind p)) (= ncol (count (:cells p))))
              (recur (inc j) groups (conj cur (:cells p)))

              (and (= :rule (:kind p)) (= widths (:widths p)) (seq cur))
              (recur (inc j) (conj groups cur) [])

              (and (= :bottom (:kind p)) (= widths (:widths p)) (seq cur))
              {:end j :groups (conj groups cur)}

              :else nil)))))))

(defn- joined-cell
  "The text of one cell: each of its lines trimmed, the blank ones dropped,
   and the rest joined with one space. The console breaks a cell at a space
   and centres a short cell on its middle line, so this gives the text back."
  [pieces]
  (str/join " " (remove str/blank? (map str/trim pieces))))

(defn split-box-tables
  "The text `s`, cut into the console tables it holds and the text between
   them: `[{:kind :text :at i :s …} {:kind :table :at i :s … :header [...]
   :rows [[...]]} …]`. `:at` is the position of the segment in `s`, and the
   `:s` of all segments together is `s`, character for character.

   THE TABLE RULE. A console table is a run of whole lines:
   1. a top border `┌──┬──┐`, with two columns or more,
   2. then content lines `│ a │ b │`, each with one cell for each column,
   3. and row lines `├──┼──┤` between the rows,
   4. to a bottom border `└──┴──┘`.
   Each border and each row line has the SAME column widths as the top. The
   console draws every border from one grid, and a box diagram does not keep
   one. That is the rule that tells a table from a diagram, a lone row line,
   or the rule line of an Insight block. A run that breaks it is text, as it
   was before this rule.

   The first row is the header when a row line follows it. A header whose
   cells are all blank is dropped.

   A CONTENT LINE IS COUNTED, NOT MEASURED. The console pads a cell by its
   width on the screen, and a wide character (a CJK letter) is two columns
   and one character. So a content line must have the right number of cells,
   and only the border lines must have the right widths.

   PUBLIC, because it is the whole of the rule and a test can ask it."
  [s]
  (let [lines  (vec (.split (str s) "\n"))
        parsed (mapv box-line lines)
        starts (vec (reductions + 0 (map #(inc (count %)) lines)))
        len    (count s)]
    (loop [i 0, from 0, out []]
      (if (>= i (count lines))
        (cond-> out (< from len) (conj {:kind :text :at from :s (subs s from)}))
        (if-let [{:keys [end groups]} (table-at parsed i)]
          (let [at     (nth starts i)
                stop   (min len (+ (nth starts end) (count (nth lines end))))
                ncol   (count (:widths (nth parsed i)))
                rows   (mapv (fn [g] (mapv (fn [c] (joined-cell (map #(nth % c) g))) (range ncol)))
                             groups)
                head?  (and (> (count rows) 1) (some (complement str/blank?) (first rows)))
                header (when (> (count rows) 1) (first rows))]
            (recur (inc end)
                   stop
                   (cond-> out
                     (< from at) (conj {:kind :text :at from :s (subs s from at)})
                     true        (conj {:kind :table :at at :s (subs s at stop)
                                        :header (when head? header)
                                        :rows (if header (subvec rows 1) rows)}))))
          (recur (inc i) from out))))))

(defn- box-table->hiccup
  "One console table as a <table>. Each cell goes through the inline rules, so
   a [[wikilink]] or a #tag in a cell works as it does in a paragraph.

   A HIGHLIGHT IS NOT PAINTED IN A CELL. A cell joins several lines, so its
   text is not one range of the source. The highlight stays in the list of
   the page."
  [{:keys [header rows]} link-target on-nav]
  (let [cell (fn [tag cls text]
               (into [tag {:class cls}]
                     (inline->pieces (.lexInline Lexer text #js {:gfm true}) link-target on-nav)))]
    [:div {:class "ikc-box-table overflow-x-auto my-2"}
     [:table {:class box-table-class}
      (when header
        [:thead (into [:tr] (map #(cell :th th-class %) header))])
      (into [:tbody]
            (for [r rows] (into [:tr] (map #(cell :td td-class %) r))))]]))

(defn split-frontmatter
  "A leading `---` block → [frontmatter-length rows body]; [0 nil s] when the
   text has none. `rows` are the block's `key: value` lines as pairs, quotes
   around a value removed. The length counts the block AND its closing line,
   so `body` starts exactly where the lexer should, and an offset into the
   source becomes an offset into the body by subtracting it.

   WHY HERE AND NOT ON THE SERVER. The highlights' offsets are into the FILE
   (highlights.edn: :start/:end against the source text), and a selection's
   anchor-text is resolved there too. Stripping the block before the read would
   move every offset the store holds; stripping it here, as one more edit the
   way wikify's are, moves nothing that is stored. George, 2026-09-08: the
   frontmatter showed in the Reading pane's preview as body text."
  [s]
  (let [s (or s "")]
    (if-let [m (re-find #"(?s)^---\r?\n(.*?)\r?\n---[ \t]*(?:\r?\n|$)" s)]
      (let [whole (first m)
            rows  (->> (str/split-lines (second m))
                       (keep (fn [line]
                               (when-let [[_ k v] (re-find #"^([A-Za-z][\w-]*):\s*(.*)$" line)]
                                 [k (str/replace (str/trim v) #"^[\"']|[\"']$" "")])))
                       vec)]
        [(count whole) rows (subs s (count whole))])
      [0 nil s])))

(defn- frontmatter-block
  "The metadata, drawn folded: one line naming the title and the source, the
   rows behind a click. Nothing here is paintable, so it carries no offsets."
  [rows]
  (let [by    (into {} rows)
        title (or (get by "title") (get by "id"))
        src   (get by "source")]
    [:details {:class "ikc-frontmatter ikc-dim text-xs mb-2"}
     [:summary {:class "cursor-pointer"}
      (str "metadata"
           (when title (str " · " title))
           (when src (str " · " src)))]
     (into [:table {:class "ikc-frontmatter-rows"}]
           (for [[k v] rows]
             [:tr [:th {:class "text-left pr-2 align-top"} k] [:td v]]))]))

(defn- render-tokens
  "Top-level block tokens → hiccup nodes. `pos` is where the first token
   begins in the (wikified) source.

   A cursor over the source: each block token consumes its own raw, so the
   next one knows where it starts. marked's TOP-LEVEL tokens tile the input in
   order — the guarantee its container children lack — which is what makes
   this a running total rather than a search.

   A PARAGRAPH THAT HOLDS A CONSOLE TABLE IS CUT IN PARTS (PLAN-ikc-v3-054-01).
   marked reads the box characters as one paragraph. split-box-tables finds
   the table in its raw text. The text before and after the table goes
   through marked again, at its own place in the source, so a highlight there
   still paints where it is. Only a top-level paragraph is cut: a table in a
   fence or in a list item stays as its author wrote it."
  [toks pos link-target on-nav hls align-src]
  (loop [ts (seq toks), pos pos, out []]
    (if-let [^js t (first ts)]
      (let [raw  (or (.-raw t) "")
            segs (when (= "paragraph" (.-type t)) (split-box-tables raw))]
        (recur (next ts)
               (+ pos (count raw))
               (if (some #(= :table (:kind %)) segs)
                 (into out
                       (mapcat (fn [{:keys [kind at] :as seg}]
                                 (if (= :table kind)
                                   [(box-table->hiccup seg link-target on-nav)]
                                   (render-tokens (array-seq (lexer (:s seg) #js {:gfm true}))
                                                  (+ pos at) link-target on-nav hls align-src))))
                       segs)
                 (if-let [node (block-token->hiccup t link-target on-nav hls pos align-src)]
                   (conj out node)
                   out))))
      out)))

(declare render*)

(defn render
  "Markdown string → hiccup [:div ...]. `link-target` (fn target→page-id) and
   `on-nav` (fn page-id→effect) wire [[wikilinks]]; both may be nil.

   `on-nav` IS CALLED FOR AN UNRESOLVED WIKILINK TOO, with page-id nil and the
   target beside it (2026-09-07). Naming a page is how you make one, so the
   click on a target that resolves to nothing is the act that creates it — and
   only the caller knows which command does that. This file reports what was
   clicked; it decides nothing. A RELATIVE FILE LINK is the one case that still
   needs a real page-id: an unresolved `[x](some/path.md)` names a file this
   vault does not hold, which is not a page to create.

   `highlights` is :highlights/for-page's result for this page. When present, the
   marked text is washed in each highlight's own colour — see the paint section
   above for what it can and cannot reach. When absent, rendering is exactly what
   it was, hashtag chips included.

   `refused` is :tags/tag-refusals’ :tags — the words a person has said are not
   tags. Each one draws as plain text rather than as a chip. When absent, every
   well formed hashtag draws a chip, which is what every surface did before
   ADR-ikc-v3-101-01."
  ([s] (render s nil nil nil nil))
  ([s link-target on-nav] (render s link-target on-nav nil nil))
  ([s link-target on-nav highlights] (render s link-target on-nav highlights nil))
  ([s link-target on-nav highlights on-tag] (render s link-target on-nav highlights on-tag nil))
  ([s link-target on-nav highlights on-tag refused]
   (binding [*on-tag*  on-tag
             *refused* (when (seq refused) (set (map str/lower-case refused)))]
     (render* s link-target on-nav highlights))))

(defn- render*
  "render's body. Split out so the binding above wraps the WHOLE build — a chip
   is made deep inside this, and a binding around one call is what makes that
   reachable without threading a fifth argument through every level."
  ([s link-target on-nav highlights]
   (let [[fm-len fm-rows src] (split-frontmatter s)
         [wsrc edits] (wikify src)
         ;; The frontmatter is the FIRST edit: everything at or past its end moves
         ;; back by its length; a highlight inside it (none in practice) would go
         ;; negative and is dropped by the paint's own range checks.
         hls         (shift-highlights
                      (if (pos? fm-len)
                        (shift-highlights highlights [{:orig-end fm-len :delta (- fm-len)}])
                        highlights)
                      edits)
         ;; Alignment exists only to place paint. With nothing to paint every
         ;; base is unused, so the per-token searches are skipped wholesale by
         ;; withholding the source they would search in.
         align-src   (when (seq hls) wsrc)
         toks        (array-seq (lexer wsrc #js {:gfm true}))]
     (into [:div {:class (block-class)}]
           (concat
            (when (seq fm-rows) [(frontmatter-block fm-rows)])
           (render-tokens toks 0 link-target on-nav hls align-src))))))
